import { useState } from 'react';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import ButtonSpinner from './ButtonSpinner';
import { busyStyle } from '../utils/busyStyle';

interface ReviewModalProps {
  lesson: Lesson;
  order: Order;
  onSubmit: (rating: number, comment?: string) => Promise<void>;
  /** Called when the player dismisses the modal (X icon or backdrop click). */
  onSkip: () => void;
}

function CloseIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * ReviewModalContent — the modal's inner card (avatar, heading, stars, comment, submit).
 *
 * Owns its own state (rating, comment, submitting). Does NOT include the
 * fixed-position backdrop wrapper, so it can be rendered inline in places like
 * the dev preview iframe where a full-viewport overlay would be invisible.
 *
 * Production callers should use the default ReviewModal export, which wraps
 * this in the fixed-position backdrop.
 */
export function ReviewModalContent({ lesson, order, onSubmit, onSkip }: ReviewModalProps) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const coachName = lesson.coachName ?? order.coachName ?? 'Coach';
  const coachFirstName = coachName.split(' ')[0] || 'your coach';
  const coachInitials = coachName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const typeLabel: Record<string, string> = {
    shot_consult: 'Shot Consult',
    single_lesson: 'Single Lesson',
    training_program: 'Training Program',
  };
  const subtitle = `${coachName} · ${typeLabel[order.type] ?? 'Lesson'}`;

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(rating, comment.trim() || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review.');
      setSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[480px] p-7" data-testid="review-modal">
      {/* Close icon — top-right */}
      <button
        type="button"
        onClick={onSkip}
        aria-label="Close"
        data-testid="review-close-btn"
        className="absolute top-2 right-2 p-2.5 text-gray-500 hover:text-gray-700 transition-colors rounded-full"
      >
        <CloseIcon size={20} />
      </button>

      {/* Coach avatar + heading */}
      <div className="flex items-center gap-4 mb-5 pr-6">
        <div className="w-10 aspect-[4/5] rounded-[8px] bg-biscayne flex items-center justify-center overflow-hidden flex-shrink-0">
          {order.coachAvatarUrl ? (
            <img src={order.coachAvatarUrl} alt={coachName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-[14px] font-bold text-white">{coachInitials}</span>
          )}
        </div>
        <div>
          <div className="text-[16px] font-extrabold text-db-text-1">How was your lesson?</div>
          <div className="text-[12px] text-db-text-2">{subtitle}</div>
        </div>
      </div>

      {/* Star rating */}
      <div className="mb-5">
        <div className="mb-2.5">
          <span className="text-[13px] font-semibold text-db-text-2">
            Rate your lesson<span className="ml-0.5 font-bold" style={{ color: '#DC2626' }}>*</span>
          </span>
        </div>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              data-testid={`review-star-${star}`}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => setRating(star)}
              className="text-[28px] leading-none transition-transform hover:scale-110"
            >
              <span className={(hovered || rating) >= star ? 'text-[#FBBF24]' : 'text-db-border'}>★</span>
            </button>
          ))}
        </div>
      </div>

      {/* Comment */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-semibold text-db-text-2">Say something about {coachFirstName}'s lesson</span>
          <span className="text-[11px] text-db-text-3">Optional</span>
        </div>
        <textarea
          data-testid="review-comment-input"
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder={`e.g. ${coachFirstName} was clear and easy to follow. I came out of the lesson with one specific change to focus on, not a lecture.`}
          rows={3}
          className="w-full border border-db-border rounded-xl px-4 py-3 text-[16px] leading-[21px] text-db-text-1 placeholder:text-gray-400 resize-none focus:outline-none focus:border-biscayne transition-colors input-glow"
        />
      </div>

      {error && (
        <div className="text-[12px] text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">{error}</div>
      )}

      {/* Required-field legend — universal form convention */}
      <div className="text-[11px] text-gray-500 mb-2">
        <span style={{ color: '#DC2626' }}>*</span> required field
      </div>

      {/* Submit — only action; full-width on every viewport */}
      <button
        aria-busy={submitting}
        style={busyStyle(submitting)}
        data-testid="review-submit-btn"
        onClick={handleSubmit}
        disabled={!rating || submitting}
        className="w-full py-3 bg-db-accent hover:bg-db-accent-hover disabled:opacity-50 rounded-xl text-[14px] font-bold text-white transition-colors btn-press"
      >
        {submitting ? <ButtonSpinner /> : 'Submit review'}
      </button>
    </div>
  );
}

/**
 * ReviewModal — production modal with full-viewport backdrop, click-out-to-dismiss,
 * and the inner content centered. Use this in real product flows.
 */
export default function ReviewModal(props: ReviewModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={props.onSkip} />
      <ReviewModalContent {...props} />
    </div>
  );
}
